import { usePageTitle } from '../hooks/usePageTitle.js';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';
import type { KbArticle, KbComment, EhrRow } from '../lib/api.js';

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function isOperatorPlus(role: string | undefined) {
  return role === 'operator' || role === 'admin';
}

// ──────────────────────────────────────────────
// Skeleton
// ──────────────────────────────────────────────

function ArticleSkeleton() {
  return (
    <div className="space-y-2 p-2">
      {[1, 2, 3].map((n) => (
        <div key={n} className="animate-pulse rounded-xl p-3" style={{ background: 'var(--bg-tertiary)' }}>
          <div className="h-3 w-3/4 rounded" style={{ background: 'var(--border)' }} />
          <div className="mt-1.5 h-2 w-1/2 rounded" style={{ background: 'var(--border)' }} />
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────
// NewArticleModal
// ──────────────────────────────────────────────

interface NewArticleModalProps {
  ehrs: EhrRow[];
  onClose: () => void;
  onCreated: (id: string) => void;
}

function NewArticleModal({ ehrs, onClose, onCreated }: NewArticleModalProps) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [ehrId, setEhrId] = useState('');
  const [body, setBody] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.createKbArticle({ ehrId: ehrId || undefined, title, body }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['kb'] });
      onCreated(res.article.id);
      onClose();
      toast.success('Article created');
    },
    onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-lg rounded-2xl p-6 space-y-4"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', boxShadow: '0 3px 40px rgba(0,0,0,0.5)' }}
      >
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>New Article</div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm"
            style={{ color: 'var(--text-dimmed)' }}
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block mb-1 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Article title…"
              className="w-full rounded-xl px-3 py-2 text-sm outline-none"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>EHR (optional)</label>
            <select
              value={ehrId}
              onChange={(e) => setEhrId(e.target.value)}
              className="w-full rounded-xl px-3 py-2 text-sm outline-none"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
            >
              <option value="">None – General</option>
              {ehrs.map((e) => (
                <option key={e.id} value={e.id}>{e.displayName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Article content…"
              rows={6}
              className="w-full rounded-xl px-3 py-2 text-sm outline-none resize-none"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-medium"
            style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate()}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', opacity: (!title.trim() || create.isPending) ? 0.6 : 1 }}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>

        {create.isError && (
          <div className="text-xs" style={{ color: 'var(--accent-red)' }}>
            {create.error instanceof Error ? create.error.message : 'Failed to create'}
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// ArticleDetail
// ──────────────────────────────────────────────

interface ArticleDetailProps {
  articleId: string;
  ehrMap: Map<string, string>;
  canEdit: boolean;
}

function ArticleDetail({ articleId, ehrMap, canEdit }: ArticleDetailProps) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draftBody, setDraftBody] = useState('');
  const [commentBody, setCommentBody] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['kb', articleId],
    queryFn: () => api.getKbArticle(articleId),
    enabled: !!articleId,
  });

  const updateArticle = useMutation({
    mutationFn: (body: string) => api.updateKbArticle(articleId, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kb'] });
      void qc.invalidateQueries({ queryKey: ['kb', articleId] });
      setEditing(false);
      toast.success('Article saved');
    },
    onError: (err) => toast.error(`Save failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  const addComment = useMutation({
    mutationFn: (body: string) => api.addKbComment(articleId, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kb', articleId] });
      setCommentBody('');
    },
    onError: (err) => toast.error(`Comment failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  if (isLoading) {
    return <div className="p-6 text-sm" style={{ color: 'var(--text-dimmed)' }}>Loading article…</div>;
  }

  if (!data) {
    return <div className="p-6 text-sm" style={{ color: 'var(--text-dimmed)' }}>Article not found.</div>;
  }

  const { article, comments } = data as { article: KbArticle; comments: KbComment[] };
  const ehrName = article.ehrId ? (ehrMap.get(article.ehrId) ?? article.ehrId) : null;

  function startEdit() {
    setDraftBody(article.body ?? '');
    setEditing(true);
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="p-6 space-y-4 flex-1">
        {/* Title row */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{article.title}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {ehrName && (
                <span
                  className="rounded-md px-2 py-0.5 text-xs font-medium"
                  style={{ background: '#0dcfb822', color: 'var(--accent-blue)', border: '1px solid #0dcfb844' }}
                >
                  {ehrName}
                </span>
              )}
              <span className="text-xs" style={{ color: 'var(--text-dimmed)' }}>
                by {article.authorName ?? 'Unknown'} · updated {formatDate(article.updatedAt)}
              </span>
            </div>
          </div>
          {canEdit && !editing && (
            <button
              type="button"
              onClick={startEdit}
              className="shrink-0 rounded-xl px-3 py-1.5 text-xs font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
            >
              Edit
            </button>
          )}
        </div>

        {/* Body */}
        {editing ? (
          <div className="space-y-2">
            <textarea
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              rows={12}
              className="w-full rounded-xl px-3 py-2 text-sm outline-none resize-none"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={updateArticle.isPending}
                onClick={() => updateArticle.mutate(draftBody)}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', opacity: updateArticle.isPending ? 0.6 : 1 }}
              >
                {updateArticle.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded-xl px-4 py-2 text-sm font-medium"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className="text-sm leading-relaxed whitespace-pre-wrap"
            style={{ color: 'var(--text-secondary)' }}
          >
            {article.body ?? <span style={{ color: 'var(--text-dimmed)' }}>No content yet.</span>}
          </div>
        )}

        {/* Divider */}
        <div className="border-t" style={{ borderColor: 'var(--border)' }} />

        {/* Comments */}
        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-dimmed)' }}>
            Comments ({comments.length})
          </div>

          {comments.map((c) => (
            <div key={c.id} className="flex gap-3">
              <div
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
              >
                {(c.authorName ?? 'U')[0]?.toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                    {c.authorName ?? 'Unknown'}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-dimmed)' }}>
                    {formatDate(c.createdAt)}
                  </span>
                </div>
                <div className="mt-0.5 text-sm whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>
                  {c.body}
                </div>
              </div>
            </div>
          ))}

          {canEdit && (
            <div className="space-y-2 pt-1">
              <textarea
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                placeholder="Add a comment…"
                rows={3}
                className="w-full rounded-xl px-3 py-2 text-sm outline-none resize-none"
                style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
              />
              <button
                type="button"
                disabled={!commentBody.trim() || addComment.isPending}
                onClick={() => addComment.mutate(commentBody)}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white"
                style={{
                  background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)',
                  opacity: (!commentBody.trim() || addComment.isPending) ? 0.6 : 1,
                }}
              >
                {addComment.isPending ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// KnowledgePage
// ──────────────────────────────────────────────

export function KnowledgePage() {
  usePageTitle('Knowledge Base');
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = isOperatorPlus(user?.role);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterEhrId, setFilterEhrId] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);

  const { data: kbData, isLoading: kbLoading } = useQuery({
    queryKey: ['kb'],
    queryFn: () => api.listKbArticles(),
  });

  const { data: ehrsData } = useQuery({
    queryKey: ['ehrs'],
    queryFn: () => api.listEhrs(),
  });

  const articles: KbArticle[] = kbData?.articles ?? [];
  const ehrs: EhrRow[] = ehrsData?.ehrs ?? [];

  // Build a map of ehrId -> displayName
  const ehrMap = new Map<string, string>(ehrs.map((e) => [e.id, e.displayName]));

  // Unique EHR IDs that actually appear in articles
  const articleEhrIds = Array.from(new Set(articles.map((a) => a.ehrId).filter(Boolean))) as string[];

  const filtered = articles.filter((a) => {
    const matchesSearch = a.title.toLowerCase().includes(search.toLowerCase());
    const matchesEhr = !filterEhrId || a.ehrId === filterEhrId;
    return matchesSearch && matchesEhr;
  });

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b px-6" style={{ borderColor: 'var(--border)' }}>
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>Documentation</div>
          <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Knowledgebase</div>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setShowNewModal(true)}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
          >
            + New Article
          </button>
        )}
      </header>

      {/* Body: two-column */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel */}
        <aside
          className="flex w-72 shrink-0 flex-col overflow-hidden border-r"
          style={{ borderColor: 'var(--border)', background: 'var(--glass-bg)', backdropFilter: 'blur(var(--glass-blur))', WebkitBackdropFilter: 'blur(var(--glass-blur))' }}
        >
          <div className="space-y-2 p-3 border-b" style={{ borderColor: 'var(--border)' }}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles…"
              className="w-full rounded-xl px-3 py-2 text-sm outline-none"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
            />
            <select
              value={filterEhrId}
              onChange={(e) => setFilterEhrId(e.target.value)}
              className="w-full rounded-xl px-3 py-2 text-sm outline-none"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
            >
              <option value="">All EHRs</option>
              <option value="__general__">General</option>
              {articleEhrIds.map((id) => (
                <option key={id} value={id}>{ehrMap.get(id) ?? id}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {kbLoading ? (
              <ArticleSkeleton />
            ) : filtered.length === 0 ? (
              <div className="p-4 text-sm text-center" style={{ color: 'var(--text-dimmed)' }}>
                No articles found.
              </div>
            ) : (
              filtered.map((a) => {
                const isSelected = a.id === selectedId;
                const ehrName = a.ehrId ? (ehrMap.get(a.ehrId) ?? a.ehrId) : 'General';
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setSelectedId(a.id)}
                    className="w-full rounded-xl p-3 text-left transition-all duration-100"
                    style={{
                      background: isSelected
                        ? 'linear-gradient(135deg, #0dcfb822 0%, #06b6d422 100%)'
                        : 'transparent',
                      boxShadow: isSelected ? 'inset 0 0 0 1px #0dcfb833' : undefined,
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.background = 'var(--bg-tertiary)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div className="truncate text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {a.title}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2">
                      {a.ehrId ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); void navigate({ to: '/ehrs/$id', params: { id: a.ehrId! } }); }}
                          className="text-xs hover:underline"
                          style={{ color: 'var(--accent-blue)' }}
                        >
                          {ehrName}
                        </button>
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--text-dimmed)' }}>{ehrName}</span>
                      )}
                      <span className="text-xs" style={{ color: 'var(--text-dimmed)' }}>·</span>
                      <span className="text-xs" style={{ color: 'var(--text-dimmed)' }}>{formatDate(a.updatedAt)}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Right panel */}
        <main className="flex-1 overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
          {!selectedId ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center space-y-2">
                <div className="text-4xl" style={{ color: 'var(--text-dimmed)' }}>◎</div>
                <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>Select an article</div>
              </div>
            </div>
          ) : (
            <ArticleDetail
              articleId={selectedId}
              ehrMap={ehrMap}
              canEdit={canEdit}
            />
          )}
        </main>
      </div>

      {/* Modal */}
      {showNewModal && (
        <NewArticleModal
          ehrs={ehrs}
          onClose={() => setShowNewModal(false)}
          onCreated={(id) => setSelectedId(id)}
        />
      )}
    </div>
  );
}
